/* eslint-disable no-underscore-dangle */

import {
  AGS_HOST_URL,
  DEFAULT_LOCALE,
  VALID_LOCALES,
  VET_HOST_URL,
  WILEYCOM_HOST_URL,
  SPECTRABASE_HOST_URL,
  DEFAULT_SITE_CODE,
} from '@config/constants';
import {
  getSiteConfig,
} from '@requests/siteConfig';
import type { SiteConfigResponse, SiteConfiguration } from '@requests/siteConfig/types';
import { logError, logWarning } from './log';
import { getSearchParamFromUrl } from './url';

export enum SiteId {
  WILEYCOM = 'wileyb2c',
  COD = 'subscription',
  VET = 'vet',
  AGS = 'ags',
  WILA = 'wila',
  AUTHOR = '0bf40307',
  SPECTRABASE = 'spectrabase',
}

const DEFAULT_SITE_ID = SiteId.WILEYCOM;
const SUBSCRIPTION_SITE_IDS = [SiteId.COD, SiteId.AGS, SiteId.VET, SiteId.SPECTRABASE];

export const COOKIE_DOMAIN = '.wiley.com';
export const AGS_COOKIE_DOMAIN = '.graphicstandards.com';
export const SPECTRABASE_COOKIE_DOMAIN = '.spectrabase.com';
const SITE_ID_URL_PARAM_KEY = 'site-id';

export const hostUrlToSiteIdMap: Readonly<Record<string, SiteId>> = {
  [WILEYCOM_HOST_URL]: SiteId.WILEYCOM,
  [VET_HOST_URL]: SiteId.VET,
  [AGS_HOST_URL]: SiteId.AGS,
  [SPECTRABASE_HOST_URL]: SiteId.SPECTRABASE,
};

const COD_CONTINUE_SHOPPING_PATH = '/shop/on-demand/chemistry';

export class SiteConfig {
  private static instance: SiteConfig | null = null;

  private readonly _siteId: SiteId;

  private readonly _hostUrl: string;

  private readonly _locale: string;

  private readonly _config?: SiteConfiguration;

  private constructor(hostUrl?: string, locale?: string, siteConfig?: SiteConfigResponse) {
    if (!hostUrl) {
      const { protocol, hostname } = window.location;
      hostUrl = `${protocol}//${hostname}`;
    }

    this._hostUrl = hostUrl;

    const localeFromUrl = window.location.pathname.slice(1, 6) as string;
    const validLocale = VALID_LOCALES.includes(localeFromUrl)
      ? localeFromUrl
      : DEFAULT_LOCALE;
    this._locale = locale || validLocale;

    if (siteConfig && Object.keys(siteConfig).length === 1) {
      this._siteId = Object.keys(siteConfig)[0] as SiteId;
    } else {
      this._siteId = DEFAULT_SITE_ID;

      const siteIdParam = getSearchParamFromUrl(SITE_ID_URL_PARAM_KEY);
      if (
        siteIdParam
        && Object.values(SiteId).includes(siteIdParam as SiteId)
      ) {
        this._siteId = siteIdParam as SiteId;
      }
    }

    this._config = siteConfig?.[this._siteId];

    Object.freeze(this);
  }

  public static async init(hostUrl?: string, locale?: string): Promise<void> {
    if (this.instance) {
      logWarning('SiteConfig: Already initialized.');
      return;
    }

    const siteConfig = await getSiteConfig(hostUrl);

    if (!siteConfig) {
      logWarning('SiteConfig: Failed to fetch site config, using default values.');
      this.instance = new SiteConfig(hostUrl, locale);
      return;
    }

    this.instance = new SiteConfig(hostUrl, locale, siteConfig);
  }

  public static getConfig(): SiteConfig {
    if (!this.instance) {
      logError('SiteConfig: Not initialized. Call init() first.');
    }

    return this.instance!;
  }

  public get siteId(): SiteId {
    return this._siteId;
  }

  public get hostUrl(): string {
    return this._hostUrl;
  }

  public get locale(): string {
    return this._locale;
  }

  public get lang(): string {
    return this._locale.split('-')[0];
  }

  public get iso(): string {
    return this._locale.split('-')[1];
  }

  public get config(): SiteConfiguration | undefined {
    return this._config;
  }

  public get isSubscriptionSite(): boolean {
    return SUBSCRIPTION_SITE_IDS.includes(this._siteId);
  }

  public get cookieDomain(): string {
    if (this._siteId === SiteId.AGS) {
      return AGS_COOKIE_DOMAIN;
    }

    if (this._siteId === SiteId.SPECTRABASE) {
      return SPECTRABASE_COOKIE_DOMAIN;
    }

    return COOKIE_DOMAIN;
  }

  public get isWileyComHosted(): boolean {
    return [SiteId.COD, SiteId.WILEYCOM, SiteId.WILA, SiteId.AUTHOR].includes(this._siteId);
  }

  public get isWileyCom(): boolean {
    return this._siteId === SiteId.WILEYCOM;
  }

  public get isWila(): boolean {
    return this._siteId === SiteId.WILA;
  }

  public get isAuthor(): boolean {
    return this._siteId === SiteId.AUTHOR;
  }

  public get isSpectrabase(): boolean {
    return this._siteId === SiteId.SPECTRABASE;
  }

  public get pricingDeterminationModel(): string | undefined {
    return this._config?.pricingDeterminationModel;
  }

  public get siteCode(): string {
    return this._config?.source ?? DEFAULT_SITE_CODE;
  }

  public static isNonLocalizedSite(): boolean {
    const { siteId } = this.getConfig();

    return siteId === SiteId.VET || siteId === SiteId.AGS;
  }

  public static buildContinueShoppingUrl(): string {
    const {
      siteId, locale, hostUrl, isWileyComHosted,
    } = this.getConfig();
    const localeStr = isWileyComHosted ? `/${locale}` : '';

    let path = '';
    if (siteId === SiteId.COD) {
      path = COD_CONTINUE_SHOPPING_PATH;
    }

    return `${hostUrl}${localeStr}${path}`;
  }

  public static buildRedirectUrl(
    redirectDomain: string,
    redirectPath: string,
    queryParams?: Record<string, string>,
  ): string {
    const {
      locale, siteId, hostUrl, isWileyComHosted,
    } = this.getConfig();
    let urlLocaleStr = '';
    let baseUrl = hostUrl;

    if (isWileyComHosted) {
      urlLocaleStr = `/${locale}`;
      baseUrl = redirectDomain;
    }

    let redirectUrl = `${baseUrl}${urlLocaleStr}${redirectPath}`;

    const params = new URLSearchParams();
    if (queryParams) {
      Object.entries(queryParams).forEach(([key, value]) => params.set(key, value));
    }

    if ([SiteId.COD, SiteId.WILA, SiteId.AUTHOR].includes(siteId)) {
      params.set(SITE_ID_URL_PARAM_KEY, siteId);
    }

    if (params.toString()) {
      redirectUrl += `?${params.toString()}`;
    }

    return redirectUrl;
  }

  /** @internal Used for testing only */
  public static reset(): void {
    this.instance = null;
  }
}
